import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@material-ui/core";
import { Add, Delete, Edit } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { CDialog, ErrorBox, HFlex, Text, useErrorContext } from "shared";
import { useMedications, useDeleteMedication } from "./queries";
import { MedicationForm } from "./components/MedicationForm";
import { getMedicationName, Medication } from "./types";

type SortField = "id" | "name" | "locales" | "variants" | "patients";
type SortDir = "asc" | "desc";

function sortMedications(meds: Medication[], field: SortField, dir: SortDir): Medication[] {
  return [...meds].sort((a, b) => {
    let cmp = 0;
    switch (field) {
      case "id":       cmp = a.id - b.id; break;
      case "name":     cmp = getMedicationName(a).localeCompare(getMedicationName(b)); break;
      case "locales":  cmp = a.translations.map(tr => tr.locale).sort().join().localeCompare(b.translations.map(tr => tr.locale).sort().join()); break;
      case "variants": cmp = a.variants.length - b.variants.length; break;
      case "patients": cmp = a.patient_count - b.patient_count; break;
    }
    return dir === "asc" ? cmp : -cmp;
  });
}

const Medications: React.VFC = () => {
  const { t } = useTranslation();
  const setError = useErrorContext();
  const { data: medications, isLoading, error } = useMedications();
  const deleteMutation = useDeleteMedication();

  const [formOpen, setFormOpen] = useState(false);
  const [editMedication, setEditMedication] = useState<Medication | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<Medication | null>(null);
  const [sortBy, setSortBy] = useState<SortField>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDir(d => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
  };

  const handleCreate = () => {
    setEditMedication(undefined);
    setFormOpen(true);
  };

  const handleEdit = (med: Medication) => {
    setEditMedication(med);
    setFormOpen(true);
  };

  const handleFormSuccess = () => {
    setFormOpen(false);
    setEditMedication(undefined);
  };

  const handleFormCancel = () => {
    setFormOpen(false);
    setEditMedication(undefined);
  };

  const handleDeleteClick = (med: Medication) => {
    setDeleteTarget(med);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
    } catch (err) {
      setError(t("medications.error_delete_failed"), err);
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(null);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box m={3}>
        <ErrorBox error={error} verbose centered dark />
      </Box>
    );
  }

  const sorted = sortMedications(medications ?? [], sortBy, sortDir);

  const sortCell = (field: SortField, label: string) => (
    <TableSortLabel active={sortBy === field} direction={sortBy === field ? sortDir : "asc"} onClick={() => handleSort(field)}>
      {label}
    </TableSortLabel>
  );

  return (
    <Box m={3}>
      <HFlex justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">{t("medications.title")}</Typography>
        <Button variant="contained" color="secondary" startIcon={<Add />} onClick={handleCreate}>
          {t("medications.new_medication")}
        </Button>
      </HFlex>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{sortCell("id", t("medications.column_id"))}</TableCell>
              <TableCell>{t("medications.column_image")}</TableCell>
              <TableCell>{sortCell("name", t("medications.column_name"))}</TableCell>
              <TableCell>{sortCell("locales", t("medications.column_locales"))}</TableCell>
              <TableCell>{sortCell("variants", t("medications.column_variants"))}</TableCell>
              <TableCell>{sortCell("patients", t("medications.column_patients"))}</TableCell>
              <TableCell align="right">{t("medications.column_actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map(med => (
              <TableRow key={med.id} hover>
                <TableCell>{med.id}</TableCell>
                <TableCell>
                  {med.image_url ? (
                    <img
                      src={med.image_url}
                      alt={getMedicationName(med)}
                      style={{
                        height: 36,
                        width: 36,
                        objectFit: "contain",
                        borderRadius: 4,
                        border: "1px solid #e0e0e0",
                      }}
                    />
                  ) : (
                    <Box
                      style={{
                        height: 36,
                        width: 36,
                        borderRadius: 4,
                        border: "1px solid #e0e0e0",
                        background: "#f5f5f5",
                      }}
                    />
                  )}
                </TableCell>
                <TableCell>{getMedicationName(med)}</TableCell>
                <TableCell>
                  <Typography variant="caption" color="textSecondary">
                    {med.translations.map(tr => tr.locale).join(", ")}
                  </Typography>
                </TableCell>
                <TableCell>{med.variants.length}</TableCell>
                <TableCell>{med.patient_count}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleEdit(med)}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDeleteClick(med)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Text secondary>{t("medications.no_medications")}</Text>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create / Edit dialog */}
      <CDialog open={formOpen} onClose={handleFormCancel} maxWidth="md" fullWidth>
        <MedicationForm medication={editMedication} onSuccess={handleFormSuccess} onCancel={handleFormCancel} />
      </CDialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
        <DialogTitle>{t("medications.confirm_delete_title")}</DialogTitle>
        <DialogContent>
          {deleteTarget && deleteTarget.patient_count > 0 && (
            <DialogContentText style={{ color: "#d32f2f", fontWeight: 500, marginBottom: 8 }}>
              {t("medications.confirm_delete_warning", { count: deleteTarget.patient_count })}
            </DialogContentText>
          )}
          <DialogContentText>
            {t("medications.confirm_delete_message", { name: deleteTarget ? getMedicationName(deleteTarget) : "" })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteMutation.isLoading}>
            {t("buttons.cancel")}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="secondary"
            disabled={deleteMutation.isLoading}
          >
            {deleteMutation.isLoading ? t("medications.deleting") : t("medications.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Medications;
